<template>
  <div class="fixed bottom-0 inset-x-0 z-50 flex flex-col pointer-events-none items-center">
    <div class="w-full md:w-1/2 lg:w-1/3 max-h-[50vh] flex flex-col pointer-events-none">
    
    <!-- Toast/Notification Area (Always Visible for recent logs) -->
    <!-- Could add transient toasts here if needed -->

    <!-- Toggle Button -->
    <div class="flex justify-center p-4 pointer-events-auto">
      <button 
        @click="toggle"
        class="bg-gray-900 text-white px-4 py-2 rounded-sm shadow-lg flex items-center space-x-2 hover:bg-gray-800 transition-colors"
      >
        <font-awesome-icon icon="bug" />
        <span class="text-xs font-bold uppercase">Debug Console</span>
        <span v-if="errorCount > 0" class="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-5 text-center">
          {{ errorCount }}
        </span>
      </button>
    </div>

    <!-- Panel Content -->
    <transition name="slide-up">
      <div 
        v-if="isOpen" 
        class="bg-gray-900 text-gray-300 font-mono text-xs shadow-2xl flex flex-col border-t border-gray-700 pointer-events-auto"
        style="height: 400px;"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-2 border-b border-gray-800 bg-gray-950">
          <div class="flex space-x-4">
            <span class="font-bold text-white">Console Logs</span>
            <button @click="clearLogs" class="hover:text-white transition-colors">
              <font-awesome-icon icon="trash-alt" class="mr-1" /> Clear
            </button>
          </div>
          <button @click="toggle" class="hover:text-white">
            <font-awesome-icon icon="minus" />
          </button>
        </div>

        <!-- Log List -->
        <div class="flex-1 overflow-auto p-4 space-y-2">
          <div 
            v-for="log in logs" 
            :key="log.id" 
            class="flex gap-2 p-2 rounded-sm hover:bg-gray-800 transition-colors group"
            :class="{
              'bg-red-900/20 border-l-2 border-red-500': log.type === 'error',
              'bg-yellow-900/20 border-l-2 border-yellow-500': log.type === 'warning',
              'bg-green-900/20 border-l-2 border-green-500': log.type === 'success',
              'bg-gray-800/30 border-l-2 border-gray-500': log.type === 'info'
            }"
          >
            <span class="text-xs text-gray-500 whitespace-nowrap pt-0.5">
              {{ formatTime(log.timestamp) }}
            </span>
            <div class="flex-1 break-all">
              <div :class="{
                'text-red-400': log.type === 'error',
                'text-yellow-400': log.type === 'warning',
                'text-green-400': log.type === 'success',
                'text-white': log.type === 'info'
              }">
                {{ log.message }}
              </div>
              <pre v-if="log.details" class="mt-1 text-[10px] text-gray-400 overflow-x-auto">{{ JSON.stringify(log.details, null, 2) }}</pre>
            </div>
          </div>
          <div v-if="logs.length === 0" class="text-center py-8 text-gray-600 italic">
            No logs recorded yet.
          </div>
        </div>
      </div>
    </transition>
  </div>
  </div>
</template>

<script setup lang="ts">
import { useDebugLogs } from '~/composables/useDebugLogs'

const { logs, isOpen, toggle, clearLogs } = useDebugLogs()

const errorCount = computed(() => logs.value.filter(l => l.type === 'error').length)

const formatTime = (date: Date) => {
  return new Date(date).toLocaleTimeString('fr-FR', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  })
}
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease-in-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
